import Prebook from '@/store/prebook.js';
import { getProductsCartInfo, purchase, facebookEvent } from '@/utils/analytics.js';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { dayjs } from '@/utils/dates/dates';
import { ThankYouTemplate } from '@desquared/wave-components-library';
import { useRouter } from 'next/router';
import { checkoutStore, fullCartStore, isEditStarted, productsToKeep, cartPrice } from '@/store/cart';
import { clearCart } from '@/utils/cart';
import { PaymentOptionStore } from '@/store/sideSheet';
import { createOrderStatusMap } from '@/utils/orders';
import { updateConfiguration } from '@/utils/getConfiguration';
import { useTranslations } from 'next-intl';
import ConfigurationStore from '@/store/configuration';
import { WaveEntityType } from '@desquared/wave-cxm-api';
import { getOrderDetails } from '@/api/orders';
import LoaderStore from '@/store/loader';
import { PREDICTION_TIMESLOT_TOKEN } from '@/utils/logic/timeslots';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { deleteCart } from '@/api/syncCart';
import Cookies from 'universal-cookie';
import { isEmpty } from 'lodash';
import { useDateFormatting } from '@/contexts/DateFormattingProvider';
import { useStoreConfiguration } from '@desquared/wave-cxm-react';
import { IsFromThankYouPage, isFromOrder } from '@/store/questionnaire';

export default function ThankYouPage({ user }) {
  const router = useRouter();
  const [price, setPrice] = useState(0);
  const [orderId, setOrderId] = useState();
  const [status, setStatus] = useState();
  const [config, setConfig] = useState();
  const [dateString, setDateString] = useState('');
  const [edit, setEdit] = useState(false);
  const { query } = router;
  const [templateId, setTemplateId] = useState();
  const [hasPredictiveTimeslot, setHasPredictiveTimeslot] = useState(false);
  const { formatter } = useContext(CurrencyContext);
  const { timeFormat } = useDateFormatting();
  const hasCheckedQuestionnaire = useRef(false);

  const t = useTranslations();

  const date = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const onContinue = () => {
    fullCartStore.update(undefined);
    isFromOrder.update(false);
    router.push('/');
  };

  const paymentMethod = PaymentOptionStore.getValue().type;
  const bankTransferOptions = PaymentOptionStore.getValue()?.bankAccountsConfig?.accounts;
  const hasQuestionnaire = process.env.NEXT_PUBLIC_CXM_HAS_QUESTIONNAIRE === 'true' || false;
  const { api: waveCxmApi } = useStoreConfiguration();

  useEffect(() => {
    if (!config) {
      return;
    }
    if (process.env.hasFacebookPixel && process.env.facebookPixelId.length > 0 && window.fbq) {
      facebookEvent('Purchase', {
        value: checkoutStore.getValue()?.totalPrice / 100, // dynamically pull the order total
        currency: config?.currency?.default,
      });
    }
  }, [config]);

  useEffect(() => {
    const ConfigurationStore$ = ConfigurationStore.subscribe(setConfig);
    Prebook.reset();
    if (hasQuestionnaire) {
      if (waveCxmApi) {
        if (user?._id && !hasCheckedQuestionnaire.current) {
          if (isFromOrder.getValue()) {
            const checkQuestionnaire = async () => {
              const response = await waveCxmApi?.client.waveEntities.checkWaveEntities({
                customerId: user?._id,
                // can be both types or either, depending on the type of the survey you want to show
                waveEntityTypes: [WaveEntityType.SURVEY, WaveEntityType.NPS_SURVEY],
                touchpoint: 'ORDER_SUBMISSION', //TODO: add the touchpoint when it is decided
              });
              hasCheckedQuestionnaire.current = true;
              if (response.successful && response.data?.length > 0) {
                setTemplateId(response.data[0].id);
              }
            };
            checkQuestionnaire();
          }
        }
      }
    }
    return () => {
      ConfigurationStore$.unsubscribe();
    };
  }, [waveCxmApi, user]);

  useEffect(() => {
    LoaderStore.setIsVisible(false);
    LoaderStore.setIsVisible(true);
    if (ConfigurationStore.isFetched) {
      setConfig(ConfigurationStore.getValue());
    } else {
      updateConfiguration().then(configuration => {
        setConfig(configuration);
      });
    }
    async function deleteCartOrder() {
      if (process.env.NEXT_PUBLIC_SYNC_CART === 'true') {
        if (!isEditStarted.getValue()) {
          await deleteCart();
        }
      }
    }
    cartPrice.update('0.00');
    deleteCartOrder();
    clearCart();
    setPrice(formatter.format(checkoutStore.getValue()?.totalPrice / 100));
    setOrderId(checkoutStore.getValue()?.friendlyId);

    purchase(
      getProductsCartInfo(checkoutStore.getValue().products),
      checkoutStore.getValue()?.totalPrice / 100,
      0,
      0,
      checkoutStore.getValue()?.friendlyId
    );

    if (checkoutStore.getValue()?.edit) setEdit(checkoutStore.getValue()?.edit);

    if (
      checkoutStore.getValue()?.timeslotsInfo &&
      checkoutStore.getValue()?.timeslotsInfo?.type === PREDICTION_TIMESLOT_TOKEN
    ) {
      setDateString(
        `${dayjs(new Date(checkoutStore.getValue().timeSlotStart))
          .tz()
          .format(`ddd ${process.env.dateFormat}`)} - ${dayjs(new Date(checkoutStore.getValue().timeSlotEnd))
          .tz()
          .format(`ddd ${process.env.dateFormat}`)}`
      );

      setHasPredictiveTimeslot(true);
    } else {
      setDateString(
        `${dayjs(new Date(checkoutStore.getValue().timeSlotStart)).tz().format(process.env.dateFormat)}, ${dayjs(
          new Date(checkoutStore.getValue().timeSlotStart)
        )
          .tz()
          .format(timeFormat)} - ${dayjs(new Date(checkoutStore.getValue().timeSlotEnd)).tz().format(timeFormat)}`
      );

      setHasPredictiveTimeslot(false);
    }

    LoaderStore.setIsVisible(true);
    if (isEditStarted?.getValue() === true) {
      productsToKeep.release();
      isEditStarted.update(false);
    } else {
      cartPrice.update('0.00');
      productsToKeep.clearValues();
    }
    LoaderStore.setIsVisible(false);
  }, [formatter]);

  useEffect(() => {
    if (!config) {
      LoaderStore.setIsVisible(false);
      return;
    }
    const orderMap = createOrderStatusMap(config);
    if (query?.orderId && config) {
      getOrderDetails(query?.orderId, config?.order?.guest?.apiKey).then(result => {
        if (result) {
          setStatus(orderMap[result.status]);
          setOrderId(result.friendlyId);
        }
      });
    } else {
      setStatus(orderMap[checkoutStore.getValue().status]);
    }
    LoaderStore.setIsVisible(false);
  }, [config, setStatus]);
  useEffect(() => {
    const handleBackNavigation = () => {
      // Redirect to a specific page when back navigation is detected
      router.push('/'); // Change '/' to the specific page you want to redirect to
    };

    // Add event listener for the 'popstate' event (triggers on back navigation)
    window.addEventListener('popstate', handleBackNavigation);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('popstate', handleBackNavigation);
    };
  }, [router]);

  return (
    <ThankYouTemplate
      customerName={user ? user?.firstName + ' ' + user?.lastName : undefined}
      orderCode={orderId}
      price={price}
      transactionNumber={query.merchantReference ? query.merchantReference : undefined}
      dateTime={dateString}
      onContinueClick={onContinue}
      bankTransferOptions={paymentMethod === 'bankTransfer' ? bankTransferOptions : undefined}
      status={status}
      successHeading={edit ? t('common.thank_you.edit_header') : t('common.thank_you.header')}
      confirmationText={edit ? t('common.thank_you.edit_description') : t('common.thank_you.description')}
      onQuestionnaireClick={
        templateId
          ? () => {
              IsFromThankYouPage.update(true);
              router.push('/questionnaire/ORDER_SUBMISSION');
            }
          : null
      }
      buttonTextQuestionnaire={templateId ? t('common.thank_you.questionnaire_button') : null}
      transactionNumberLabel={t('common.thank_you.order_code')}
      customerNameLabel={t('common.thank_you.customer_name')}
      orderCodeLabel={t('common.thank_you.transaction_number')}
      priceLabel={t('common.thank_you.price_label')}
      dateTimeLabel={hasPredictiveTimeslot ? t('common.thank_you.predictive_text') : t('common.thank_you.date_label')}
      greeting={t('common.thank_you.greeting')}
      buttonText={t('common.thank_you.button_text')}
      bankTransferText={t('common.thank_you.bank_transfer_text')}
    />
  );
}
